import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addMinutes, format, getDay, parse, startOfHour } from 'date-fns';
import { FormikConfig } from 'formik';
import { useState } from 'react';
import { useSession } from 'src/contexts/SessionContext';
import {
  AppointmentStatus,
  ConflictCheckResponse,
} from 'src/models/appointment/_types';
import {
  checkConflicts,
  createAppointment,
  updateAppointment,
} from 'src/models/appointment/appointment';
import {
  createScheduledAppointment,
  updateScheduledAppointment,
} from 'src/models/scheduled-appointment/scheduled-appointment';
import { fetchServices } from 'src/models/service/service';
import toast from 'src/utils/toast';
import * as Yup from 'yup';

import { EditEventFormValues } from './_types';

import type { Appointment } from 'src/models/appointment/_types';
import type { Service } from 'src/models/service/_types';
import type { Identifier } from 'src/types/utilityTypes';


// Recurring save scope options - matches v2 modal-new-appointment.js lines 1655-1678
export type RecurringSaveScope = 'this' | 'all';

interface UseEditEventDialogOptions {
  appointment?: Appointment | null;
  initialDate?: Date | null;
  initialServiceId?: Identifier | null;
  initialTrainerId?: Identifier | null;
  onClose: () => void;
  onSaveSuccess?: (appointment: Appointment) => void;
}

const DEFAULT_DURATION_MINUTES = 60;

export const useEditEventDialog = ({
  appointment,
  initialDate,
  initialServiceId,
  initialTrainerId,
  onClose,
  onSaveSuccess,
}: UseEditEventDialogOptions) => {
  const queryClient = useQueryClient();
  const { user } = useSession();
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [conflictData, setConflictData] =
    useState<ConflictCheckResponse | null>(null);
  const [pendingValues, setPendingValues] =
    useState<EditEventFormValues | null>(null);

  // Recurring prompt state - v2 lines 1655-1716
  const [scopeDialogOpen, setScopeDialogOpen] = useState(false);
  const [notifyDialogOpen, setNotifyDialogOpen] = useState(false);
  const [saveScope, setSaveScope] = useState<RecurringSaveScope>('this');

  // Check if this is a recurring session
  const isRecurring = !!appointment?.scheduledAppointmentId;

  // Fetch services for service selection
  const { data: services = [], isLoading: servicesLoading } = useQuery<
    Service[]
  >({
    queryKey: ['services', 'options'],
    queryFn: () => fetchServices({ includePrivate: true }),
    staleTime: 60000,
    gcTime: 60000,
  });

  // Find the selected service to get its default time
  const getServiceById = (serviceId: Identifier | null): Service | undefined => {
    if (!serviceId) return undefined;
    return services.find((s) => s.id === serviceId);
  };

  // Calculate initial values
  const getInitialValues = (): EditEventFormValues => {
    const startTime =
      appointment?.startTime ||
      initialDate ||
      startOfHour(new Date(Date.now() + 60 * 60 * 1000));

    const service = getServiceById(
      appointment?.serviceId || initialServiceId || null
    );
    const duration = service?.defaultTime || DEFAULT_DURATION_MINUTES;
    const endTime = appointment?.endTime || addMinutes(startTime, duration);

    const dayOfWeek = getDay(startTime);

    return {
      serviceId: appointment?.serviceId || initialServiceId || null,
      locationId: appointment?.locationId || null,
      startTime,
      endTime,
      maxVisits: appointment?.maxVisits || null,
      description: appointment?.description || '',
      notes: appointment?.notes || '',

      primaryTrainerIds:
        appointment?.primaryTrainerIds ||
        (initialTrainerId ? [initialTrainerId] : user?.id ? [user.id] : []),
      secondaryTrainerIds: appointment?.secondaryTrainerIds || [],
      noTrainers: appointment?.noTrainers || false,

      isRecurring: appointment?.scheduledAppointmentId ? true : false, // New appointments start as non-recurring
      recurringType: appointment?.scheduledAppointment?.recurringType ? appointment.scheduledAppointment.recurringType : 'week' as const,
      recurringCount: appointment?.scheduledAppointment?.recurringCount ? appointment.scheduledAppointment.recurringCount : 1,
      recurringDays: appointment?.scheduledAppointment?.days ? appointment.scheduledAppointment.days : [dayOfWeek],
      recurringDayNumbers: appointment?.scheduledAppointment?.recurringDayNumbers ? appointment.scheduledAppointment.recurringDayNumbers : [],
      endAt: appointment?.scheduledAppointment?.endAt
        ? typeof appointment.scheduledAppointment.endAt === 'string'
          ? parse(appointment.scheduledAppointment.endAt, 'yyyy-MM-dd', new Date())
          : appointment.scheduledAppointment.endAt
        : null,
      bookRecurring: appointment?.scheduledAppointment?.bookRecurring ? appointment.scheduledAppointment.bookRecurring : false,

      noResources: appointment?.noResources || false,
      serviceResourceRules: (appointment?.serviceResourceRules || []).map((rule) => ({
        ...rule,
        // Normalize resourceId to number for consistent comparison with parentResourceOptions
        resourceId: rule.resourceId ? Number(rule.resourceId) : null,
        // Normalize subResourceIds to numbers as well
        subResourceIds: (rule.subResourceIds || []).map((id) => Number(id)),
      })),
    };
  };

  const initialValues = getInitialValues();

  // Validation schema
  const validationSchema = Yup.object({
    serviceId: Yup.mixed().required('Service is required'),
    locationId: Yup.mixed().nullable(),
    startTime: Yup.date().required('Start time is required'),
    endTime: Yup.date()
      .required('End time is required')
      .min(Yup.ref('startTime'), 'End time must be after start time'),
    maxVisits: Yup.number().nullable().positive('Must be a positive number'),
    description: Yup.string(),
    notes: Yup.string(),
    primaryTrainerIds: Yup.array().when('noTrainers', {
      is: false,
      then: (schema) =>
        schema.min(1, 'At least one primary trainer is required'),
      otherwise: (schema) => schema,
    }),
    secondaryTrainerIds: Yup.array(),
    noTrainers: Yup.boolean(),
    isRecurring: Yup.boolean(),
    recurringType: Yup.string().oneOf([
      'week',
      'month_on_date',
      'month_on_day',
    ]),
    recurringCount: Yup.number().min(1),
    recurringDays: Yup.array(),
    recurringDayNumbers: Yup.array(),
    endAt: Yup.date().nullable(),
    bookRecurring: Yup.boolean(),
    noResources: Yup.boolean(),
    serviceResourceRules: Yup.array(),
  });

  const invalidateScheduleQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['appointments'] });
    queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
    queryClient.invalidateQueries({ queryKey: ['calendar-schedules'] });
    queryClient.invalidateQueries({ queryKey: ['scheduledAppointment'] });
  };

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (values: Partial<Appointment>) => createAppointment(values),
    onSuccess: (data) => {
      invalidateScheduleQueries();
      toast.success('Session created successfully');
      onSaveSuccess?.(data);
      onClose();
    },
    onError: () => {
      toast.error('Failed to create session');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (values: Partial<Appointment>) => updateAppointment(values),
    onSuccess: (data) => {
      invalidateScheduleQueries();
      toast.success('Session updated successfully');
      onSaveSuccess?.(data);
      onClose();
    },
    onError: () => {
      toast.error('Failed to update session');
    },
  });

  // Build appointment payload from form values
  // Note: saveScheduledAppointment is a client-side flag only, NOT sent to API
  // In v2, it controls whether to also save the scheduledAppointment model separately
  const buildPayload = (
    values: EditEventFormValues,
    options?: { disableEmail?: boolean }
  ): Partial<Appointment> => {
    const payload: Partial<Appointment> = {
      status: AppointmentStatus.Scheduled,
      disableEmail: options?.disableEmail ?? false,
      serviceId: values.serviceId as Identifier,
      overrideMaxVisits: false,
      secondaryTrainerIds: values.noTrainers ? [] : values.secondaryTrainerIds,
      primaryTrainerIds: values.noTrainers ? [] : values.primaryTrainerIds,
      startTime: values.startTime,
      endTime: values.endTime,
      serviceResourceRules: values.serviceResourceRules || [],
      noResources: values.noResources,
      noTrainers: values.noTrainers,
      zoom: null,
      cancelledAt: null,
      locationId: (values.locationId || undefined) as Identifier,
      description: values.description,
      notes: values.notes,
    };

    // Only include maxVisits if editing (for group classes)
    if (appointment?.id && values.maxVisits) {
      payload.maxVisits = values.maxVisits;
    }

    return payload;
  };

  // Proceed with save after conflict check passes
  // Matches v2 modal-new-appointment.js lines 1800-1920
  const proceedWithSave = async (
    values: EditEventFormValues,
    options?: { disableEmail?: boolean; saveScheduledAppointment?: boolean }
  ) => {
    const payload = buildPayload(values, {
      disableEmail: options?.disableEmail,
    });

    // Check if we need to create a new scheduledAppointment
    const needsNewScheduledAppointment =
      values.isRecurring && !appointment?.scheduledAppointmentId;

    // If user selected "All Future Sessions", handle the scheduled appointment update.
    // v2 modal-new-appointment.js lines 1822-1908: if the appointment is the first
    // session in the series, update the existing scheduled appointment in place.
    // Otherwise, "fracture" the series by creating a NEW scheduled appointment
    // starting from the current session's date, with originalScheduledId pointing
    // to the old one. The Rails backend then reassigns all future appointments
    // to the new series and ends the old one.
    if (
      options?.saveScheduledAppointment &&
      appointment?.scheduledAppointmentId
    ) {
      const appointmentDate = format(values.startTime, 'yyyy-MM-dd');
      const isFirstSession =
        appointment.scheduledAppointment?.startAt === appointmentDate;

      if (isFirstSession) {
        await updateScheduledAppointment({
          scheduledAppointmentId: appointment.scheduledAppointmentId,
          serviceId: values.serviceId as number,
          locationId: values.locationId as number | null,
          primaryTrainerIds: values.noTrainers ? [] : values.primaryTrainerIds,
          secondaryTrainerIds: values.noTrainers
            ? []
            : values.secondaryTrainerIds,
          startTime: values.startTime,
          endTime: values.endTime,
          endAt: values.endAt ? format(values.endAt, 'yyyy-MM-dd') : null,
          description: values.description,
          notes: values.notes,
          noResources: values.noResources,
          serviceResourceRules: values.serviceResourceRules,
          disableEmail: options.disableEmail,
        });
      } else {
        // Fracture: create a new scheduled appointment starting from this session
        // v2 lines 1870-1907
        await createScheduledAppointment({
          startAt: appointmentDate,
          recurringStartAt: appointmentDate,
          endAt: values.endAt ? format(values.endAt, 'yyyy-MM-dd') : null,
          days: appointment.scheduledAppointment?.days || values.recurringDays,
          bookRecurring: values.bookRecurring,
          serviceId: values.serviceId as number,
          locationId: (values.locationId || undefined) as number,
          primaryTrainerIds: values.noTrainers ? [] : values.primaryTrainerIds,
          secondaryTrainerIds: values.noTrainers
            ? []
            : values.secondaryTrainerIds,
          startTime: values.startTime,
          endTime: values.endTime,
          description: values.description,
          notes: values.notes,
          noResources: values.noResources,
          serviceResourceRules: values.serviceResourceRules,
          recurringType: values.recurringType,
          recurringCount: values.recurringCount,
          recurringDayNumbers: values.recurringDayNumbers,
          maxVisitDays: appointment.scheduledAppointment?.maxVisitDays || [],
          originalScheduledId: appointment.scheduledAppointmentId,
        });
      }

      invalidateScheduleQueries();
      toast.success('All future sessions updated successfully');
      onSaveSuccess?.(appointment);
      onClose();
    } else if (needsNewScheduledAppointment) {
      // Creating a new recurring series (either new appointment or converting existing to recurring)
      // First create the scheduledAppointment, then create/update the appointment with the scheduledAppointmentId
      const scheduledAppointment = await createScheduledAppointment({
        serviceId: values.serviceId as number,
        locationId: values.locationId as number | null,
        primaryTrainerIds: values.noTrainers ? [] : values.primaryTrainerIds,
        secondaryTrainerIds: values.noTrainers
          ? []
          : values.secondaryTrainerIds,
        startTime: values.startTime,
        endTime: values.endTime,
        startAt: format(values.startTime, 'yyyy-MM-dd'),
        recurringStartAt: format(values.startTime, 'yyyy-MM-dd'),
        endAt: values.endAt ? format(values.endAt, 'yyyy-MM-dd') : null,
        recurringType: values.recurringType,
        recurringCount: values.recurringCount,
        days: values.recurringDays,
        recurringDayNumbers: values.recurringDayNumbers,
        bookRecurring: values.bookRecurring,
        description: values.description,
        notes: values.notes,
        noResources: values.noResources,
        serviceResourceRules: values.serviceResourceRules,
      });

      if (appointment?.id) {
        // Converting existing appointment to recurring - update it with the new scheduledAppointmentId
        await updateMutation.mutateAsync({
          ...payload,
          id: appointment.id,
          scheduledAppointmentId: scheduledAppointment.id,
          rescheduleDate: format(values.startTime, 'yyyy-MM-dd'),
        });
      } else {
        // Creating a new recurring appointment
        await createMutation.mutateAsync({
          ...payload,
          scheduledAppointmentId: scheduledAppointment.id,
          rescheduleDate: format(values.startTime, 'yyyy-MM-dd'),
        });
      }
    } else if (appointment?.id) {
      // Update existing appointment (non-recurring or already recurring single session update)
      await updateMutation.mutateAsync({ ...payload, id: appointment.id });
    } else {
      // Create new non-recurring appointment
      await createMutation.mutateAsync(payload);
    }
  };

  // Form submit handler - checks conflicts first, then shows recurring prompts if needed
  const handleSubmit = async (values: EditEventFormValues) => {
    try {
      // Check for conflicts before saving
      const conflictResult = await checkConflicts({
        primaryTrainerIds: values.noTrainers ? [] : values.primaryTrainerIds,
        secondaryTrainerIds: values.noTrainers
          ? []
          : values.secondaryTrainerIds,
        startTime: values.startTime,
        endTime: values.endTime,
        appointmentId: appointment?.id,
        locationId: values.locationId,
        noResources: values.noResources,
        serviceResourceRules: values.serviceResourceRules,
        // Add recurring params if creating recurring
        ...(values.isRecurring
          ? {
              recurringType: values.recurringType,
              recurringCount: values.recurringCount,
              startAt: format(values.startTime, 'yyyy-MM-dd'),
              endAt: values.endAt ? format(values.endAt, 'yyyy-MM-dd') : null,
              selectedDays: values.recurringDays,
            }
          : {}),
      });

      if (conflictResult.hasConflicts) {
        // Store conflict data and show dialog - matches v2 showConflictDialog()
        setConflictData(conflictResult);
        setPendingValues(values);
        setConflictDialogOpen(true);
        return;
      }

      // Store values for potential recurring prompts
      setPendingValues(values);

      // For existing recurring sessions, show scope dialog first - v2 lines 1655-1678
      if (isRecurring && appointment?.id) {
        setScopeDialogOpen(true);
        return;
      }

      // For new appointments or non-recurring, proceed directly
      // New appointments don't need notify prompt (v2 lines 1683-1685)
      if (!appointment?.id) {
        await proceedWithSave(values, { disableEmail: false });
      } else {
        // Non-recurring existing appointment - show notify dialog
        setNotifyDialogOpen(true);
      }
    } catch (error) {
      toast.error('Unable to save session. Please try again.');
    }
  };

  // Override conflicts and save anyway - v2 showConflictDialog() "Override Conflict" button
  const handleOverrideConflicts = async () => {
    if (pendingValues) {
      setConflictDialogOpen(false);
      setConflictData(null);

      // After overriding conflicts, check if we need recurring prompts
      if (isRecurring && appointment?.id) {
        setScopeDialogOpen(true);
      } else if (appointment?.id) {
        // Non-recurring existing appointment - show notify dialog
        setNotifyDialogOpen(true);
      } else {
        // New appointment - save directly
        await proceedWithSave(pendingValues, { disableEmail: false });
        setPendingValues(null);
      }
    }
  };

  // Cancel conflict override - v2 showConflictDialog() "Cancel" button
  const handleCancelConflict = () => {
    setConflictDialogOpen(false);
    setConflictData(null);
    setPendingValues(null);
  };

  // Handle scope dialog selection - v2 lines 1660-1676
  const handleScopeSelect = (scope: RecurringSaveScope) => {
    setSaveScope(scope);
    setScopeDialogOpen(false);
    // After selecting scope, show notify dialog
    setNotifyDialogOpen(true);
  };

  // Handle notify dialog selection and proceed with save - v2 lines 1688-1716
  const handleNotifySelect = async (notify: boolean) => {
    setNotifyDialogOpen(false);

    if (pendingValues) {
      await proceedWithSave(pendingValues, {
        disableEmail: !notify,
        saveScheduledAppointment: saveScope === 'all',
      });
      setPendingValues(null);
      setSaveScope('this'); // Reset for next save
    }
  };

  // Cancel recurring dialogs
  const handleCancelRecurringPrompts = () => {
    setScopeDialogOpen(false);
    setNotifyDialogOpen(false);
    setPendingValues(null);
    setSaveScope('this');
  };

  const formikConfig: FormikConfig<EditEventFormValues> = {
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  };

  // Helper to update end time when service or start time changes
  const updateEndTimeForService = (
    serviceId: Identifier | null,
    startTime: Date,
    setFieldValue: (field: string, value: Date) => void
  ) => {
    const service = getServiceById(serviceId);
    const duration = service?.defaultTime || DEFAULT_DURATION_MINUTES;
    setFieldValue('endTime', addMinutes(startTime, duration));
  };

  return {
    formikConfig,
    services,
    servicesLoading,
    getServiceById,
    updateEndTimeForService,
    isSubmitting: createMutation.isPending || updateMutation.isPending,
    isEditing: !!appointment?.id,
    isRecurring,
    // Conflict handling - v2 showConflictDialog()
    conflictDialogOpen,
    conflictData,
    handleOverrideConflicts,
    handleCancelConflict,
    // Recurring prompts - v2 lines 1655-1716
    scopeDialogOpen,
    setScopeDialogOpen,
    notifyDialogOpen,
    setNotifyDialogOpen,
    handleScopeSelect,
    handleNotifySelect,
    handleCancelRecurringPrompts,
  };
};
